import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Layout } from '../components/Layout';
import { ChatMessage } from '../components/ChatMessage';
import { ChatInput } from '../components/ChatInput';
import { SessionsPanel } from '../components/SessionsPanel';
import {
  streamCoachMessage,
  listCoachSessions,
  getCoachSession,
  saveCoachSession,
} from '../services/coach';
import { useUserState } from '../hooks/useUserState';
import type { Session } from '../types';

interface Message {
  role: 'user' | 'coach';
  content: string;
}

const OPENING_MESSAGE: Message = {
  role: 'coach',
  content:
    "Hi! I'm your PM Coach. Ask me anything about project management frameworks, TPM best practices, or agile methodologies.",
};

function CoachPage() {
  const [messages, setMessages] = useState<Message[]>([OPENING_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>(() =>
    crypto.randomUUID(),
  );
  const [usageRemaining, setUsageRemaining] = useState<number | null>(null);
  const [error, setError] = useState<'limit' | 'general' | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const { tier, usageRemaining: initialUsage } = useUserState();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  // Mirrors `messages` so async callbacks (stream onDone) read the latest
  // transcript rather than a stale closure value.
  const messagesRef = useRef<Message[]>(messages);
  const isEmpty = messages.length <= 1;

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (messages.length <= 1) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, loading]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const handler = () => {
      const offsetFromBottom =
        window.innerHeight - viewport.height - viewport.offsetTop;
      if (inputWrapperRef.current) {
        inputWrapperRef.current.style.paddingBottom = `${Math.max(16, offsetFromBottom + 16)}px`;
      }
    };

    viewport.addEventListener('resize', handler);
    viewport.addEventListener('scroll', handler);
    return () => {
      viewport.removeEventListener('resize', handler);
      viewport.removeEventListener('scroll', handler);
    };
  }, []);

  // Seed the usage counter from /users/me on first load. Stream responses
  // take over once the user sends a message. Pro users report null, so we
  // only sync a concrete number.
  useEffect(() => {
    if (initialUsage !== null) {
      setUsageRemaining(initialUsage);
    }
  }, [initialUsage]);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data = await listCoachSessions();
      setSessions(data);
    } catch {
      // silently fail — sessions panel is non-critical
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  async function handleSelectSession(selectedSessionId: string) {
    try {
      const session = await getCoachSession(selectedSessionId);
      setSessionId(selectedSessionId);
      setMessages(
        session.messages.map(m => ({
          role: m.role === 'user' ? 'user' : 'coach',
          content: m.content,
        })),
      );
      setPanelOpen(false);
    } catch {
      // silently fail — resume is best-effort
    }
  }

  async function handleSend() {
    const prompt = input.trim();
    if (!prompt || loading) return;

    setMessages(m => [
      ...m,
      { role: 'user', content: prompt },
    ]);
    setInput('');
    setLoading(true);
    setError(null);

    await streamCoachMessage(
      { session_id: sessionId, prompt },
      text => {
        flushSync(() => {
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && last.role === 'coach') {
              updated[updated.length - 1] = {
                ...last,
                content: last.content + text,
              };
            } else {
              updated.push({ role: 'coach', content: text });
            }
            return updated;
          });
        });
      },
      async usageRemainingFromStream => {
        if (usageRemainingFromStream !== null) {
          setUsageRemaining(usageRemainingFromStream);
        }
        setLoading(false);
        try {
          await saveCoachSession(sessionId, messagesRef.current);
        } catch {
          // non-critical — a failed save must not disrupt the chat
        }
        fetchSessions();
      },
      err => {
        if (err === 'LIMIT_REACHED') {
          setError('limit');
          setUsageRemaining(0);
        } else {
          setError('general');
        }
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === 'coach' && last.content === '') {
            updated.pop();
          }
          return updated;
        });
        setLoading(false);
      },
    );
  }

  function handleNewChat() {
    setMessages([OPENING_MESSAGE]);
    setSessionId(crypto.randomUUID());
    setInput('');
    setError(null);
    setPanelOpen(false);
  }

  return (
    <Layout mode="sidebar" usageRemaining={usageRemaining} tier={tier}>
      <div
        style={{
          flex: 1,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 24px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-primary)',
            flexShrink: 0,
          }}
        >
          <h1
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '20px',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--text-primary)',
              margin: 0,
            }}
          >
            PM Coach
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => setPanelOpen(p => !p)}
              title="Chat history"
              style={{
                background: panelOpen ? 'var(--accent-bg)' : 'transparent',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '6px 10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: panelOpen ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: '13px',
                fontWeight: 500,
                transition: 'all 0.2s',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              History
            </button>
            <button
              onClick={handleNewChat}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                color: 'var(--text-secondary)',
                background: 'none',
                border: '1px solid var(--border)',
                cursor: 'pointer',
              }}
            >
              + New chat
            </button>
          </div>
        </header>

        <div
          ref={messagesContainerRef}
          style={{
            flex: isEmpty ? '0 1 auto' : 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '20px 24px',
            paddingBottom: isEmpty ? '30vh' : '20px',
          }}
        >
          <div style={{ maxWidth: '880px', margin: '0 auto' }}>
            {messages.map((m, i) => (
              <ChatMessage key={i} role={m.role} content={m.content} />
            ))}

            {loading && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '14px',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    color: 'var(--text-muted)',
                    fontSize: '14px',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      background: 'var(--accent)',
                      borderRadius: '50%',
                      marginRight: '6px',
                      animation: 'pulse 1.2s ease-in-out infinite',
                    }}
                  />
                  Thinking…
                </div>
              </div>
            )}

            {error === 'general' && (
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: '10px',
                  background: 'rgba(255,115,115,0.08)',
                  border: '1px solid rgba(255,115,115,0.3)',
                  color: '#ff7373',
                  fontSize: '13px',
                  marginBottom: '12px',
                }}
              >
                Something went wrong reaching the coach. Try again in a moment.
              </div>
            )}
          </div>
        </div>

        <div ref={inputWrapperRef} style={{ flexShrink: 0, paddingBottom: '16px' }}>
          <ChatInput
            value={input}
            onChange={setInput}
            onSend={handleSend}
            loading={loading}
            usageRemaining={usageRemaining}
            disabled={error === 'limit'}
          />
        </div>

        {panelOpen && (
          <SessionsPanel
            sessions={sessions}
            activeSessionId={sessionId}
            isLoading={sessionsLoading}
            onSelectSession={handleSelectSession}
            onNewChat={handleNewChat}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}</style>
    </Layout>
  );
}

export default CoachPage;
